import moment from 'moment'
import type { IntlShape } from 'react-intl'
import type { TripDay, TripStop } from 'tripPlanner/api/types'
import { formatStopDateRange } from 'tripPlanner/utils/chatDates'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'

export function isIsoDate(value: string): boolean {
  return moment(value, ISO_DATE_FORMAT, true).isValid()
}

export function getStopDurationDayCount(startDate: string, endDate: string): number | undefined {
  if (!isIsoDate(startDate) || !isIsoDate(endDate)) return undefined

  const start = moment(startDate, ISO_DATE_FORMAT, true)
  const end = moment(endDate, ISO_DATE_FORMAT, true)
  if (end.isBefore(start, 'day')) return undefined

  return end.diff(start, 'days') + 1
}

export function getSortedStopDays(stop: TripStop): Array<TripDay> {
  return [...(stop.days ?? [])].sort((a, b) => a.dayNumber - b.dayNumber)
}

export function getExistingStopDurationDayCount(stop: TripStop): number | undefined {
  if (stop.startDate && stop.endDate) {
    return getStopDurationDayCount(stop.startDate, stop.endDate) ?? 1
  }

  const days = getSortedStopDays(stop)
  const firstDay = days[0]
  const lastDay = days[days.length - 1]
  if (firstDay && lastDay) {
    if (typeof firstDay.dayNumber === 'number' && typeof lastDay.dayNumber === 'number') {
      return Math.max(1, lastDay.dayNumber - firstDay.dayNumber + 1)
    }

    return days.length
  }

  return undefined
}

export function getStopDateRangeLabel(stop: TripStop): string | undefined {
  const days = getSortedStopDays(stop)
  const startDate = stop.startDate ?? days.find(day => day.date)?.date
  const endDate = stop.endDate ?? [...days].reverse().find(day => day.date)?.date
  if (!startDate || !endDate) return undefined
  return formatStopDateRange(startDate, endDate)
}

export function formatStopDurationLabel(intl: IntlShape, dayCount: number): string {
  return intl.formatMessage(
    {
      id: 'yVSWducQemMjHdM_IrwS',
      defaultMessage: '{dayCount, plural, one {# day} other {# days}}',
      description: 'Duration label for a stop heading in the trip planner list view',
    },
    { dayCount },
  )
}

export function getStopDurationLabel(intl: IntlShape, stop: TripStop): string | undefined {
  const dayCount = getExistingStopDurationDayCount(stop)
  return dayCount ? formatStopDurationLabel(intl, dayCount) : undefined
}

export function getStopTimingSubtitle(intl: IntlShape, stop: TripStop): string | undefined {
  const parts = [
    getStopDurationLabel(intl, stop),
    getStopDateRangeLabel(stop),
  ].filter(Boolean)

  return parts.length > 0 ? parts.join(' · ') : undefined
}
